import { useEffect, useState } from 'react';
import { NextRouter } from 'next/router';
import { useUserV3 } from '@fusion/auth';
import { TelemetryPlugin } from '../types';
import { telemetryCtx } from '../contexts/telemetry';

type TelemetryProps = {
  router: NextRouter;
  plugins: Array<TelemetryPlugin>;

  /** Optionally delay rendering of child components until all plugins are initialised */
  blockUntilReady?: boolean;

  /** Component used to display loading state when blockUntilReady is true */
  LoadingComponent?: React.ElementType;

  children: React.ReactNode;
};

/**
 * Attaches telemetry plugins
 * NOTE: Some plugins require the @fusion/auth provider in order to send user details
 */
export const TelemetryProvider: React.ElementType<TelemetryProps> = ({
  router,
  plugins,
  blockUntilReady = false,
  LoadingComponent = () => <>Loading...</>,
  children,
}) => {
  const { data: user } = useUserV3();
  const [{ isReady, pluginCtx }, setTelemetryState] = useState<{ isReady: boolean; pluginCtx: Record<string, any> }>({
    isReady: false,
    pluginCtx: {},
  });

  const getPluginCtx = (pluginName: string) => {
    return pluginCtx[pluginName];
  };

  // Plugin initialisation
  useEffect(() => {
    const initCtx = {};

    // Call each plugins initialisation function and capture
    // the returned value for addition to the context
    for (const { name, init } of plugins) {
      if (init) {
        initCtx[name] = init({ user });
      }
    }

    // Register handler for page view events
    // Note: arbitrary delay is to give the page a chance to update the <title> before sending the event
    const handleRouteChange = (isInitial = false) => {
      for (const { onRouteChange } of plugins) {
        if (onRouteChange) {
          setTimeout(() => {
            onRouteChange({
              isInitial,
              url: window.location.pathname + window.location.search,
              title: document.title,
            });
          }, 500);
        }
      }
    };

    setTelemetryState({
      isReady: true,
      pluginCtx: initCtx,
    });

    // Send an initial route change event
    handleRouteChange(true);

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  return (
    <telemetryCtx.Provider
      value={{
        isReady,
        getPluginCtx,
      }}
    >
      {blockUntilReady && !isReady ? <LoadingComponent /> : children}
    </telemetryCtx.Provider>
  );
};
